import { LayoutGrid } from '@blueprinthq/joy'
import {
  GridItem,
  Text,
  Box,
  HStack,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { useHistory } from 'react-router-dom'
import { useStoreState, useStoreActions } from 'easy-peasy'
import React, { useMemo, useEffect, useState } from 'react'
import {
  EndSessionModal,
  SessionAgenda,
  PsychotherapyNoteEditor,
  Footer,
  AINotetakerPanel,
  StartRecordingDialog,
  PanelHeader,
  PanelBody
} from './components'
import { SessionRecapDto } from '@core/api/models'
import { useSessionControllerPostSessionMarkComplete } from '@core/api'
import { HighRiskToggle } from '@components'
import { usePanelManager, useAudioCapture, useExperienceManager } from '@hooks'
import { datadogLogs } from '@datadog/browser-logs'
import moment from 'moment'
import { isMobile } from 'react-device-detect'
import { SessionWrapper } from './session-wrapper'
import { trackEvent } from '@lib/clinician-tracking'

type SessionActivityV2PropsT = {
  client: {
    id: string
    first_name: string
    last_name: string
    isChildAge: boolean
    isHighRisk: boolean
    is_demo: boolean
  }
  recap?: SessionRecapDto
  sessionId: string
  aiNotetakerEnabled?: boolean
  refetchRecap: () => void
}

function determineGridColumns({
  isSessionAgendaOpen,
  isSessionNotesOpen
}: {
  isSessionAgendaOpen: boolean
  isSessionNotesOpen: boolean
}) {
  const columns = {
    aiNotetaker: {
      start: { base: 1, sm: 1, md: 1 },
      end: { base: 13, sm: 13, md: 13 }
    },
    sessionAgenda: {
      start: { base: 1, sm: 1, md: 4 },
      end: { base: 13, sm: 13, md: 10 }
    },
    psychotherapyNotes: {
      start: { base: 1, sm: 1, md: 10 },
      end: { base: 13, sm: 13, md: 13 }
    }
  }

  if (isSessionAgendaOpen && !isSessionNotesOpen) {
    columns.aiNotetaker.end.md = 4
    columns.sessionAgenda.start.md = 4
    columns.sessionAgenda.end.md = 13
  } else if (!isSessionAgendaOpen && isSessionNotesOpen) {
    columns.aiNotetaker.end.md = 10
    columns.psychotherapyNotes.start.md = 10
  } else if (isSessionAgendaOpen && isSessionNotesOpen) {
    columns.aiNotetaker.end.md = 4
    columns.sessionAgenda.start.md = 4
    columns.sessionAgenda.end.md = 10
    columns.psychotherapyNotes.start.md = 10
  }

  return columns
}

const SessionActivityV2 = ({
  client,
  recap,
  sessionId,
  aiNotetakerEnabled = false
}: SessionActivityV2PropsT) => {
  const { user } = useStoreState(state => state.auth)

  const history = useHistory()
  const toast = useToast()

  const [isEndingSession, setIsEndingSession] = useState(false)
  const [isEndModalOpen, setEndModalOpen] = useState(false)
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [noteType, setNoteType] = useState('soap')
  const [isTelehealthLocal, setIsTelehealth] = useState(false)
  const [isUsingHeadphones, setIsUsingHeadphones] = useState(false)
  const {
    documentationAutomationFreeTierSessionLimitReached,
    isAdmin,
    isSafari,
    isExtension,
    isExtensionMinimized
  } = useExperienceManager()

  const {
    startRecording,
    testAudioInputs,
    hasMicAccess,
    isMuted,
    disableEchoCancelation,
    isRecording,
    isRecordingLoading,
    startContentShare,
    stopRecording,
    logData: baseLogData,
    hasDetectedAudio,
    showMicWarning
  } = useAudioCapture()

  const logData = {
    ...baseLogData,
    clientId: client.id,
    recordingDuration
  }

  const {
    isSessionAgendaOpen,
    isSessionNotesOpen,
    toggleSessionNotes,
    toggleSessionAgenda
  } = usePanelManager()

  const {
    isOpen: isStartRecordingOpen,
    onOpen: openStartRecording,
    onClose: closeStartRecording
  } = useDisclosure()

  const {
    mutateAsync: endSession,
    isLoading: isLoadingEndSession
  } = useSessionControllerPostSessionMarkComplete()

  useEffect(() => {
    if (user) {
      setNoteType(user?.clinic?.organization?.default_note_type || 'soap')
    }
  }, [user])

  useEffect(() => {
    const fetchData = async () => {
      if (recap?.chimeMeetingId) {
        await startRecording({
          isTelehealth: isTelehealthLocal,
          isUsingHeadphones,
          noteType
        })
      }
    }
    fetchData()
  }, [recap?.recordingStartedAt])

  const openDocAutomationPlanModal = useStoreActions(
    (actions: any) => actions.modals.chooseDocumentationAutomationPlan.openModal
  )

  const openDocAutomationSessionLimitRecachedModal = useStoreActions(
    (actions: any) => actions.modals.docAutomationSessionLimitReached.openModal
  )

  useEffect(() => {
    if (!hasMicAccess && isStartRecordingOpen) {
      closeStartRecording()
    }
  }, [hasMicAccess, isStartRecordingOpen])

  useEffect(() => {
    if (recap?.isTelehealth) {
      setIsTelehealth(true)
    }
  }, [recap?.isTelehealth])

  useEffect(() => {
    if (isTelehealthLocal && !isMobile && !isSafari) {
      disableEchoCancelation(true)
    } else {
      disableEchoCancelation(false)
    }
  }, [isTelehealthLocal, isMobile])

  useEffect(() => {
    let interval: undefined | NodeJS.Timeout

    if (recap?.recordingStartedAt) {
      const startingSeconds = moment
        .utc()
        .diff(moment(recap.recordingStartedAt), 'seconds')
      setRecordingDuration(startingSeconds)
    }

    if (isRecording) {
      interval = setInterval(() => {
        setRecordingDuration(prev => prev + 1)
      }, 1000)
    }

    return () => {
      clearInterval(interval)
    }
  }, [isRecording, recap?.recordingStartedAt])

  useEffect(() => {
    if (isMuted) {
      datadogLogs.logger.info('[Session] Microphone muted', logData)
    } else {
      datadogLogs.logger.info('[Session] Microphone unmuted', logData)
    }
  }, [isMuted])

  useEffect(() => {
    if (hasDetectedAudio) {
      datadogLogs.logger.info('[Session] Audio detected', logData)
    }
  }, [hasDetectedAudio])

  useEffect(() => {
    if (showMicWarning) {
      datadogLogs.logger.info('[Session] Showing Mic Warning', logData)
    } else {
      datadogLogs.logger.info('[Session] Hiding Mic Warning', logData)
    }
  }, [showMicWarning])

  useEffect(() => {
    ;(window as any).Intercom?.('update', {
      hide_default_launcher: true
    })

    return () => {
      ;(window as any).Intercom?.('update', {
        hide_default_launcher: false
      })
    }
  }, [])

  const handleBeforeUnload = useMemo(() => {
    return () => {
      datadogLogs.logger.info('[Session] Page refresh attempt', logData)
      return 'Did you finish the session?'
    }
  }, [logData])

  useEffect(() => {
    window.onbeforeunload = handleBeforeUnload
    return () => {
      window.onbeforeunload = () => {}
    }
  }, [handleBeforeUnload])

  const handleStartRecordingClick = async () => {
    if (documentationAutomationFreeTierSessionLimitReached && !isRecording) {
      if (isAdmin) {
        openDocAutomationPlanModal()
      } else {
        openDocAutomationSessionLimitRecachedModal()
      }

      return
    }

    await testAudioInputs()

    openStartRecording()
  }

  const handleStartRecording = async () => {
    try {
      if ('wakeLock' in navigator) {
        // @ts-ignore
        await navigator.wakeLock.request('screen')
      }
    } catch (err) {
      datadogLogs.logger.warn('[Session] Unable to request wake lock', {
        err,
        ...logData
      })
    }

    closeStartRecording()

    await startRecording({
      noteType,
      isTelehealth: isTelehealthLocal,
      isUsingHeadphones
    })

    trackEvent('Session -> Started Recording')
  }

  const handleCloseStartRecording = async () => {
    await stopRecording()
    closeStartRecording()
  }

  const columns = determineGridColumns({
    isSessionAgendaOpen,
    isSessionNotesOpen
  })

  const handleEndSession = async ({ noteType, useDemoNotesWithEBC }: any) => {
    setIsEndingSession(true)
    try {
      await stopRecording()
      await endSession(
        {
          id: sessionId,
          data: {
            noteType,
            useDemoNotesWithEBC
          }
        },
        {
          onSuccess() {
            trackEvent('Completed Session', { sessionId })
          }
        }
      )
    } catch (err) {
      datadogLogs.logger.info('[Session] Error ending session', logData)
    }

    setIsEndingSession(false)
    if (isExtension) {
      history.push(
        `/extension/patient/${client.id}/completed-session/${sessionId}`
      )
    } else {
      history.push(
        `/patient/${client.id}/completed-session/${sessionId}${
          client.is_demo ? '?isDemo=true' : ''
        }`
      )
    }
  }

  const handleDiscardSession = async () => {
    try {
      await stopRecording()
    } catch (err) {
      datadogLogs.logger.info('[Session] Error resetting meeting', logData)
    }
    if (isExtension) {
      history.replace('/extension/start-session')
    } else {
      history.replace('/sessions')
    }
    toast({
      title: 'Session discarded',
      status: 'success',
      isClosable: true,
      duration: 2000
    })
  }

  const onOpenEndModal = () => setEndModalOpen(true)
  const onCloseEndModal = () => setEndModalOpen(false)

  return (
    <SessionWrapper
      isExtension={isExtension}
      isExtensionMinimized={isExtensionMinimized}
      hasStartedRecording={isRecording}
    >
      <Box
        h={{ base: '100%', md: '100dvh' }}
        overflowY={{ base: 'auto', md: 'hidden' }}
        style={{
          margin: 'auto 16px'
        }}
      >
        <LayoutGrid style={{ gap: '16px', height: '100%', margin: 'auto 0' }}>
          <GridItem
            colStart={columns.aiNotetaker.start}
            colEnd={columns.aiNotetaker.end}
            margin="auto 0"
          >
            <AINotetakerPanel
              sessionId={sessionId}
              hasStartedRecording={isRecording}
              isRecordingLoading={isRecordingLoading || isEndingSession}
              startRecording={handleStartRecordingClick}
              aiNotetakerEnabled={aiNotetakerEnabled}
              recordingDuration={recordingDuration}
            />
          </GridItem>
          {isSessionAgendaOpen && !isExtension && (
            <GridItem
              colStart={columns.sessionAgenda.start}
              colEnd={columns.sessionAgenda.end}
              bg="white"
              borderRadius="8px"
              overflow="hidden"
            >
              <PanelHeader title="Session plan" onClose={toggleSessionAgenda} />
              <PanelBody>
                <HStack gap="8px">
                  <Text
                    fontSize="32px"
                    as="h2"
                    fontWeight="bold"
                    lineHeight="normal"
                  >
                    {`${client.first_name} ${client.last_name}`}
                  </Text>
                  <HighRiskToggle
                    clientId={client.id}
                    isHighRisk={client.isHighRisk}
                    isInert
                  />
                </HStack>
                <Box h="24px" />
                <SessionAgenda
                  client={client}
                  recap={recap}
                  sessionId={sessionId}
                />
              </PanelBody>
            </GridItem>
          )}
          {isSessionNotesOpen && !isExtension && (
            <GridItem
              colStart={columns.psychotherapyNotes.start}
              colEnd={columns.psychotherapyNotes.end}
              borderRadius="8px"
              overflow="hidden"
              bg="#FEFAF3"
            >
              <PanelHeader
                title="Psychotherapy note"
                onClose={toggleSessionNotes}
              />
              <PanelBody>
                <PsychotherapyNoteEditor
                  recap={recap}
                  sessionId={sessionId}
                  v2Enabled
                />
              </PanelBody>
            </GridItem>
          )}
        </LayoutGrid>
      </Box>
      <Box minH="76px" style={{ marginTop: 0 }} />
      <Footer
        aiNotetakerEnabled={aiNotetakerEnabled}
        onSessionEnd={onOpenEndModal}
        isRecordingLoading={isRecordingLoading || isEndingSession}
        hasStartedRecording={isRecording}
        startRecording={handleStartRecordingClick}
        onShareAudio={startContentShare}
      />
      <StartRecordingDialog
        isOpen={isStartRecordingOpen}
        onClose={handleCloseStartRecording}
        startRecording={handleStartRecording}
        setIsTelehealth={setIsTelehealth}
        setIsUsingHeadphones={setIsUsingHeadphones}
        onShareAudio={startContentShare}
      />
      <EndSessionModal
        isOpen={isEndModalOpen}
        onClose={onCloseEndModal}
        noteType={recap?.noteType || ''}
        sessionId={sessionId}
        onEnd={handleEndSession}
        onDiscard={handleDiscardSession}
        isEnding={isEndingSession}
      />
    </SessionWrapper>
  )
}

export default SessionActivityV2
